import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from '@remix-run/node'
import { json, redirect } from '@remix-run/node'
import { Link, useFetcher, useLoaderData } from '@remix-run/react'
import { MailIcon } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation, Trans } from 'react-i18next'

import { Form } from '~/helpers/form'
import { makeTranslatedTitle } from '~/helpers/pages'
import { makeMainPath } from '~/helpers/routes'
import { HONEYPOT_FIELD_NAME, sellerLoginSchema } from '~/schemas/seller'
import {
  sellerAuthenticator,
  requireNoSessionSeller,
} from '~/services/auth-seller.server'
import {
  buildCommitApexSessionHeaders,
  getApexSession,
} from '~/services/session-apex.server'
import { Alert, Button, Input } from '~/ui'

export const loader = async ({ request }: LoaderFunctionArgs) => {
  await requireNoSessionSeller(request)

  const session = await getApexSession(request)

  const authEmail = session.get('auth:email')
  if (authEmail) throw redirect(makeMainPath('/login/confirm'))

  const authError = session.get(
    sellerAuthenticator.sessionErrorKey as 'auth:error'
  )

  return json(
    { authError, title: await makeTranslatedTitle('page.login_confirm.title') },
    {
      // commit session to clear any possible error message
      headers: await buildCommitApexSessionHeaders(session),
    }
  )
}

export async function action({ request }: ActionFunctionArgs) {
  await requireNoSessionSeller(request)

  const formData = await request.clone().formData()
  if (formData.get(HONEYPOT_FIELD_NAME)) {
    const session = await getApexSession(request)
    session.flash(sellerAuthenticator.sessionErrorKey, {
      message: 'errors.could_not_send_email_generic',
    })
    return redirect(makeMainPath('/login'), {
      headers: await buildCommitApexSessionHeaders(session),
    })
  }

  await sellerAuthenticator.authenticate('OTP', request, {
    successRedirect: makeMainPath('/login/confirm'),
    failureRedirect: makeMainPath('/login'),
  })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: data?.title },
]

export default function LoginPage() {
  let { authError } = useLoaderData<typeof loader>()
  const authErrorMessage = authError?.message
  const { t } = useTranslation()

  return (
    <div className="flex w-full grow flex-col">
      <div className="flex grow flex-col items-center justify-center">
        <h1 className="text-foreground mb-1 text-center text-xl font-medium">
          {t('page.login.greeting_seller')}
        </h1>
        <h2 className="text-md text-foreground/48 xs:mb-12 mb-6 text-center">
          {t('page.login.greeting_subtitle_seller')}
        </h2>

        <LoginForm authErrorMessage={authErrorMessage} />
      </div>

      <p className="text-highlight/48 mx-auto mt-6 mb-4 max-w-xs text-center text-xs">
        <Trans
          i18nKey="page.login.terms_and_privacy_police"
          t={t}
          values={{
            terms: t('page.login.terms'),
            privacy: t('page.login.privacy'),
          }}
          components={{
            termsLink: (
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              <Link
                className="underline underline-offset-2"
                to="https://protocol.store/terms-and-conditions"
              />
            ),
            privacyLink: (
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              <Link
                className="underline underline-offset-2"
                to="https://protocol.store/privacy-policy"
              />
            ),
          }}
        />
      </p>
    </div>
  )
}

function LoginForm({ authErrorMessage }: { authErrorMessage?: string }) {
  const { t } = useTranslation()
  const fetcher = useFetcher()

  const hasUserInteracted = useRef(false)

  return (
    <Form schema={sellerLoginSchema} className="w-full max-w-[350px]">
      {({ Field, register, clearErrors, formState }) => {
        const emailError = formState.errors.email?.message

        return (
          <>
            <Field
              name={HONEYPOT_FIELD_NAME}
              aria-hidden="true"
              className="pointer-events-none absolute left-[-9999px] h-0 w-0 overflow-hidden opacity-0"
            >
              {() => (
                <label>
                  <input
                    type="text"
                    {...register(HONEYPOT_FIELD_NAME)}
                    tabIndex={-1}
                    autoComplete="off"
                    defaultValue=""
                  />
                </label>
              )}
            </Field>
            <Field name="email" className="flex flex-col gap-2">
              {() => {
                return (
                  <>
                    <Input.Group size="lg">
                      <Input.Prefix>
                        <MailIcon className="opacity-64" />
                      </Input.Prefix>

                      <Input
                        type="email"
                        {...register('email')}
                        placeholder={t('page.login.email_placeholder')!}
                        onInput={() => {
                          clearErrors()
                          hasUserInteracted.current = true
                        }}
                        invalid={!!(emailError || authErrorMessage)}
                      />
                    </Input.Group>
                  </>
                )
              }}
            </Field>

            {(emailError ||
              (!hasUserInteracted.current && authErrorMessage)) && (
              <Alert
                className="mt-3"
                type="error"
                title={t(emailError || authErrorMessage || '')}
              />
            )}

            <div className="mt-6 flex flex-col items-stretch">
              <Button
                size="lg"
                type="submit"
                isLoading={fetcher.state !== 'idle'}
              >
                {t('action.get_started_or_login')!}
              </Button>
            </div>
          </>
        )
      }}
    </Form>
  )
}
